// PropertySimulation.tsx - Main Component
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Card, CardContent, Typography } from '@mui/material';
import { TrendingUp as PropertyIcon } from '@mui/icons-material';
import type { RootState, AppDispatch } from 'app/store';
import {
  runSimulation,
  SimulationPayload
} from 'slices/fundManagementInvestment/simulationDashboardSlice';

// Import modular components
import PropertyFormComponent from './PropertyForm';
import PropertyResults from './PropertyResults';
import {
  PropertyForm,
  PropertyExpandedSections,
  PropertySectionKey,
  SimulationResult // Fixed import
} from '../simulationTypes';

const PropertySimulation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, result } = useSelector((s: RootState) => s.simulationDashboard);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [expandedSections, setExpandedSections] = useState<PropertyExpandedSections>({
    basic: true,
    financing: false,
    income: false,
    market: false,
    risk: false
  });

  const [form, setForm] = useState<PropertyForm>({
    iterations: 10000,
    years: 15,
    assetId: 'prop_melbourne_001',
    assetName: 'Melbourne Investment Property',
    purchasePrice: 850000,
    stampDuty: 42500,
    legalFees: 3500,
    inspection: 1200,
    other: 2000,
    loanAmount: 680000,
    interestRateMin: 0.058,
    interestRateMax: 0.072,
    termYears: 30,
    monthlyIncomeAmount: 3200,
    incomeGrowthMin: 0.025,
    incomeGrowthMax: 0.045,
    councilRates: 3200,
    insurance: 2400,
    maintenance: 3500,
    strataFees: 4800,
    managementFeePercent: 0.085,
    transactionCostPercent: 0.006,
    capitalGrowthMin: 0.038,
    capitalGrowthMax: 0.058,
    volatilityStdDev: 0.15,
    recessionProb: 0.12,
    recessionImpact: -0.25,
    vacancyRate: 0.08,
    defaultProbability: 0.03,
    liquidityDiscount: 0.08
  });

  const handleInputChange = (field: keyof PropertyForm, value: number | string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section: PropertySectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Build API payload based on the provided structure
  const buildPayload = (): SimulationPayload => ({
    simulation_config: {
      iterations: form.iterations,
      years: form.years,
      percentile_choices: ['P10', 'P50', 'P90']
    },
    assets: [
      {
        asset_id: form.assetId,
        asset_name: form.assetName,
        asset_type: 'property' as const,
        initial_investment: {
          purchase_price: form.purchasePrice,
          upfront_costs: {
            stamp_duty: form.stampDuty,
            legal_fees: form.legalFees,
            inspection: form.inspection,
            other: form.other
          }
        },
        financing: {
          loan_amount: form.loanAmount,
          interest_rate: {
            type: 'range',
            min: form.interestRateMin,
            max: form.interestRateMax
          },
          term_years: form.termYears,
          payment_frequency: 'monthly'
        },
        income_streams: {
          primary_income: {
            amount: form.monthlyIncomeAmount,
            frequency: 'monthly' as const,
            growth_rate: {
              type: 'range',
              min: form.incomeGrowthMin,
              max: form.incomeGrowthMax
            }
          }
        },
        expenses: {
          fixed_annual: {
            council_rates: form.councilRates,
            insurance: form.insurance,
            maintenance: form.maintenance,
            strata_fees: form.strataFees
          },
          variable_percent: {
            management_fee_percent: form.managementFeePercent,
            transaction_cost_percent: form.transactionCostPercent
          }
        },
        market_assumptions: {
          capital_growth: {
            type: 'range',
            min: form.capitalGrowthMin,
            max: form.capitalGrowthMax
          },
          volatility: {
            annual_std_deviation: form.volatilityStdDev
          },
          market_cycles: {
            recession_probability: form.recessionProb,
            recession_impact: form.recessionImpact
          }
        },
        risk_factors: {
          vacancy_rate: form.vacancyRate,
          default_probability: form.defaultProbability,
          liquidity_discount: form.liquidityDiscount
        }
      }
    ]
  });

  const runPropertySimulation = () => {
    const payload = buildPayload();
    dispatch(runSimulation(payload));
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1.5 } }}>
      <Grid container spacing={2}>
        {/* Form - 30% */}
        <Grid item xs={12} lg={3.5}>
          <PropertyFormComponent
            form={form}
            handleInputChange={handleInputChange}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            runPropertySimulation={runPropertySimulation}
            loading={loading}
            error={error}
          />
        </Grid>

        {/* Results - 70% */}
        <Grid item xs={12} lg={8.5}>
          {result ? (
            <PropertyResults
              result={result as SimulationResult} // Fixed type cast
              form={form}
              reportRef={reportRef as React.RefObject<HTMLDivElement>}
              exportMenuAnchor={exportMenuAnchor}
              setExportMenuAnchor={setExportMenuAnchor}
            />
          ) : (
            <Card
              elevation={1}
              sx={{
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <PropertyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Property Investment Simulator Ready
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure parameters and run simulation for comprehensive property analysis
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PropertySimulation;
